import { useEffect, useState } from 'react';

export interface ApproveModalItem {
  name: string;
  spec?: string;
  quantity: number;
  unit: string;
}

export interface ApproveModalBatchOrder {
  orderSn: string;
  createdAt?: string;
  department?: string;
  totalAmount?: number;
  isBackorder?: boolean;
}

interface ApproveModalProps {
  isOpen: boolean;
  orderSn: string;
  department?: string;
  orderPerson?: string;
  createdAt?: string;
  items?: ApproveModalItem[];
  /** 批量审批：传入选中数量后展示批量摘要 */
  batchCount?: number;
  batchOrders?: ApproveModalBatchOrder[];
  submitting?: boolean;
  onCancel: () => void;
  onApprove: (remark: string) => void;
  onReject: (remark: string) => void;
}

export default function ApproveModal({
  isOpen,
  orderSn,
  department = '',
  orderPerson = '',
  createdAt = '',
  items = [],
  batchCount,
  batchOrders = [],
  submitting = false,
  onCancel,
  onApprove,
  onReject,
}: ApproveModalProps) {
  const [remark, setRemark] = useState('');
  const [error, setError] = useState('');
  const isBatch = Number(batchCount) > 0;

  useEffect(() => {
    if (isOpen) {
      setRemark('');
      setError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleReject = () => {
    const text = remark.trim();
    if (!text) {
      setError('需要填写审批意见');
      return;
    }
    setError('');
    onReject(text);
  };

  const handleApprove = () => {
    setError('');
    onApprove(remark.trim());
  };

  const formatSubmitTime = (time: string) => {
    if (!time) return '-';
    const d = new Date(time);
    if (Number.isNaN(d.getTime())) return time;
    return d.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatOrderDate = (time?: string) => {
    if (!time) return '-';
    const d = new Date(time);
    if (Number.isNaN(d.getTime())) return time;
    return d.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).replace(/\//g, '-');
  };

  const formatAmount = (amount?: number, isBackorder?: boolean) => {
    const value = Number(amount);
    if (!Number.isFinite(value)) return '-';
    const abs = Math.abs(value).toFixed(2);
    return isBackorder ? `-¥${abs}` : `¥${abs}`;
  };

  const batchTotalAmount = batchOrders.reduce((sum, order) => {
    const value = Number(order.totalAmount);
    if (!Number.isFinite(value)) return sum;
    return sum + (order.isBackorder ? -Math.abs(value) : value);
  }, 0);

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40" onClick={onCancel}>
      <div
        className={`bg-background-50 rounded-xl w-full mx-4 shadow-2xl max-h-[90vh] flex flex-col ${
          isBatch ? 'max-w-2xl' : 'max-w-lg'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-background-200 flex items-center gap-2 shrink-0">
          <i className="ri-shield-check-line text-primary-500 text-lg" />
          <h3 className="text-sm font-semibold text-foreground-900">
            {isBatch ? '批量审批' : '审批操作'}
          </h3>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          {isBatch ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-sm text-foreground-700">
                  已选择 <strong className="text-foreground-900">{batchCount}</strong> 笔代办订单，将统一提交审批结果。
                </p>
                <p className="text-xs text-foreground-500 whitespace-nowrap">
                  合计金额{' '}
                  <strong className={batchTotalAmount < 0 ? 'text-orange-600' : 'text-primary-600'}>
                    {batchTotalAmount < 0
                      ? `-¥${Math.abs(batchTotalAmount).toFixed(2)}`
                      : `¥${batchTotalAmount.toFixed(2)}`}
                  </strong>
                </p>
              </div>
              <div className="border border-background-200 rounded-lg overflow-hidden max-h-56 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 z-[1]">
                    <tr className="bg-background-100 text-foreground-600">
                      <th className="py-2 px-3 text-left font-medium whitespace-nowrap">订单号</th>
                      <th className="py-2 px-3 text-left font-medium whitespace-nowrap">日期</th>
                      <th className="py-2 px-3 text-left font-medium whitespace-nowrap">科室</th>
                      <th className="py-2 px-3 text-right font-medium whitespace-nowrap">采购金额</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchOrders.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-6 px-3 text-center text-foreground-400">
                          暂无选中订单
                        </td>
                      </tr>
                    ) : (
                      batchOrders.map((order, idx) => (
                        <tr
                          key={`${order.orderSn}-${idx}`}
                          className={`border-t border-background-100 ${
                            idx % 2 === 0 ? 'bg-white' : 'bg-background-50/40'
                          }`}
                        >
                          <td className="py-2 px-3 text-foreground-800 font-medium whitespace-nowrap">
                            {order.orderSn || '-'}
                          </td>
                          <td className="py-2 px-3 text-foreground-600 whitespace-nowrap">
                            {formatOrderDate(order.createdAt)}
                          </td>
                          <td className="py-2 px-3 text-foreground-700">
                            <span className="line-clamp-2">{order.department || '-'}</span>
                          </td>
                          <td
                            className={`py-2 px-3 text-right font-semibold whitespace-nowrap ${
                              order.isBackorder ? 'text-orange-600' : 'text-primary-600'
                            }`}
                          >
                            {formatAmount(order.totalAmount, order.isBackorder)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-foreground-500 mb-0.5">订单号</p>
                  <p className="font-medium text-foreground-900 break-all">{orderSn || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-0.5">提交时间</p>
                  <p className="font-medium text-foreground-900">{formatSubmitTime(createdAt)}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-0.5">科室名称</p>
                  <p className="font-medium text-foreground-900">{department || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-0.5">采购人</p>
                  <p className="font-medium text-foreground-900">{orderPerson || '-'}</p>
                </div>
              </div>

              <div>
                <p className="text-xs font-medium text-foreground-700 mb-2">订单物资</p>
                <div className="border border-background-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-background-100 text-foreground-600">
                        <th className="py-2 px-3 text-left font-medium">物资</th>
                        <th className="py-2 px-3 text-left font-medium">规格</th>
                        <th className="py-2 px-3 text-right font-medium whitespace-nowrap">数量</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="py-6 px-3 text-center text-foreground-400">
                            暂无物资明细
                          </td>
                        </tr>
                      ) : (
                        items.map((item, idx) => (
                          <tr
                            key={`${item.name}-${idx}`}
                            className={`border-t border-background-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-background-50/40'}`}
                          >
                            <td className="py-2 px-3 text-foreground-800 align-top">
                              <span className="line-clamp-2">{item.name || '-'}</span>
                            </td>
                            <td className="py-2 px-3 text-foreground-500 align-top">
                              <span className="line-clamp-2">{item.spec || '-'}</span>
                            </td>
                            <td className="py-2 px-3 text-right text-foreground-800 whitespace-nowrap align-top">
                              {item.quantity}
                              {item.unit || ''}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          <div>
            <label className={`block text-xs font-medium mb-1.5 ${error ? 'text-red-600' : 'text-foreground-700'}`}>
              审批批注
            </label>
            <textarea
              value={remark}
              onChange={(e) => {
                const next = e.target.value;
                setRemark(next);
                // 有输入内容时恢复默认状态
                if (error && next.trim()) setError('');
              }}
              placeholder="填写批注或驳回原因..."
              maxLength={500}
              rows={3}
              disabled={submitting}
              aria-invalid={!!error}
              className={`w-full px-3 py-2.5 text-sm border rounded-lg outline-none resize-none disabled:opacity-60 transition-colors ${
                error
                  ? 'border-red-400 bg-red-50/30 focus:border-red-500 focus:ring-1 focus:ring-red-100'
                  : 'border-background-300 bg-transparent focus:border-primary-400 focus:ring-1 focus:ring-primary-200'
              }`}
            />
            <div className="mt-1.5 flex items-start justify-between gap-2">
              {error ? (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line" />
                  {error}
                </p>
              ) : (
                <p className="text-xs text-foreground-500">同意时批注为选填；拒绝时批注为驳回原因（必填）。</p>
              )}
              <p className="text-xs text-foreground-400 shrink-0">{remark.length}/500</p>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-background-200 flex items-center justify-end gap-2 shrink-0">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-background-300 text-foreground-600 text-sm hover:bg-background-100 transition-colors whitespace-nowrap disabled:opacity-50"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleReject}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-background-50 text-sm font-medium transition-colors whitespace-nowrap disabled:opacity-50"
            style={{ backgroundColor: '#ed4014' }}
          >
            <i className="ri-close-line mr-1" />
            {isBatch ? '批量拒绝' : '拒绝'}
          </button>
          <button
            type="button"
            onClick={handleApprove}
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap disabled:opacity-50"
          >
            {submitting ? (
              <i className="ri-loader-4-line animate-spin mr-1" />
            ) : (
              <i className="ri-check-line mr-1" />
            )}
            {isBatch ? '批量同意' : '同意'}
          </button>
        </div>
      </div>
    </div>
  );
}
